import { useState } from "react";

import { Button } from "shared/ui/shadcn/button";
import { Input } from "shared/ui/shadcn/input";
import CalendarForm, { CalendarDateI } from "shared/ui/calendar-form";
import { getFullEntityName } from "shared/lib/helpers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "shared/ui/shadcn/select";
import { USA_STATES } from "@/constants/states";

export interface HomefixFormFieldsI {
  address: string;
  city: string;
  state: string;
  zip_code: string;
  product: string;
}

// Keep in sync with backend: common/homefix/consts.go HomefixProducts
const HOMEFIX_PRODUCTS = [
  "ROOFING",
  "WINDOWS",
  "SIDING",
  "DOORS",
  "SLIDING GLASS DOOR",
  "GUTTERS",
  "TRIM",
  "SOLAR",
  "SHINGLES",
];

// Keep in sync with backend: common/homefix/zip_to_office.go HomefixStates
// (generated from the service-area file by scripts/gen_homefix_zips).
const HOMEFIX_STATES = ["VA", "MD", "DC", "NC", "PA", "WV"];

// Contacts store person_state as a full name ("Virginia", "District of
// Columbia") while the dropdown — and the backend — use 2-letter codes.
// Map a stored value to its Homefix code so the prefill actually selects an
// option; return "" for anything outside the service area so the Select shows
// its placeholder instead of an unmatched (invisible) value.
export const normalizeHomefixState = (input?: string | null): string => {
  const value = (input || "").trim();

  if (!value) {
    return "";
  }

  const upper = value.toUpperCase();

  if (HOMEFIX_STATES.includes(upper)) {
    return upper;
  }

  const match = USA_STATES.find(
    (s) =>
      s.label.toLowerCase() === value.toLowerCase() ||
      s.value.toLowerCase() === value.toLowerCase()
  );

  const code =
    match?.abbr || (value.toLowerCase() === "district of columbia" ? "DC" : "");

  return HOMEFIX_STATES.includes(code) ? code : "";
};

interface AvailableAE {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  scheduling_link: string;
}

interface FallbackStepPropsI {
  aeName: string;
  handleOpenSchedulingLink: () => void;
  // False when no usable scheduling URL resolves — the "still access the AE's
  // scheduling link" affordance would otherwise be a silent no-op. Reachable
  // since the attribution AE list stopped requiring a scheduling link.
  canOpenSchedulingLink?: boolean;
  isLoading: boolean;
  isReschedule?: boolean;
  isAsyncBooking?: boolean;
  onRetry: () => void;
  onDateChange: (date: CalendarDateI) => void;
  // GLE-8421: notes already typed in an earlier step, to prefill the form.
  initialNotes: string;
  onNotesChange: (notes: string) => void;
  onBookManually: () => void;
  showAESelector?: boolean;
  availableAEs?: AvailableAE[];
  selectedAE?: AvailableAE | null;
  onSelectAE?: (aeId: string) => void;
  isLoadingAEs?: boolean;
  homefixFields?: HomefixFormFieldsI;
  onHomefixFieldChange?: (
    field: keyof HomefixFormFieldsI,
    value: string
  ) => void;
  // canConfirm gates the "Confirmed" checkbox to reps with can_confirm_homefix.
  canConfirm?: boolean;
  confirmed?: boolean;
  onConfirmedChange?: (checked: boolean) => void;
}

export const FallbackStep = ({
  aeName,
  handleOpenSchedulingLink,
  canOpenSchedulingLink = true,
  isLoading,
  isReschedule,
  isAsyncBooking,
  onRetry,
  onDateChange,
  initialNotes,
  onNotesChange,
  onBookManually,
  showAESelector,
  availableAEs,
  selectedAE,
  onSelectAE,
  isLoadingAEs,
  homefixFields,
  onHomefixFieldChange,
  canConfirm,
  confirmed,
  onConfirmedChange,
}: FallbackStepPropsI) => {
  // Captured once: CalendarForm feeds `notes` to Formik with
  // enableReinitialize, so a live value would reset the form per keystroke.
  const [notesSeed] = useState(initialNotes);

  return (
    <div>
      {isAsyncBooking ? (
        <div className="mb-4 space-y-2 rounded-lg bg-blue-50 p-4 text-blue-800 typography-body-4">
          <p>
            Please enter the meeting details below to complete your booking.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 space-y-2 rounded-lg bg-amber-50 p-4 text-amber-800 typography-body-4">
            <p>
              We couldn't automatically detect the meeting from{" "}
              {aeName ? `${aeName}'s` : "the AE's"} calendar. This can happen if
              the calendar hasn't synced yet, or a calendar API hasn't sent us
              fresh data.
            </p>

            {canOpenSchedulingLink && (
              <p>
                You can{" "}
                <span
                  className="cursor-pointer font-semibold text-amber-900 underline hover:underline"
                  onClick={handleOpenSchedulingLink}
                >
                  still access the AE's scheduling link
                </span>{" "}
                if you're not sure if you have{" "}
                {isReschedule ? "rescheduled" : "booked"} a meeting.
              </p>
            )}
          </div>

          <Button className="mb-6 w-full" onClick={onRetry}>
            Try pulling the event again
          </Button>

          <p className="mb-4 text-muted-foreground typography-body-4">
            <span className="font-semibold text-black">Note:</span> If the event
            still isn't detected, please enter the meeting details manually
            below.
          </p>
        </>
      )}

      {showAESelector ? (
        <div className="mb-4 space-y-2">
          <label className="text-gray-700 typography-body-4-medium">
            Who was this meeting assigned to?
          </label>

          {isLoadingAEs ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-gray-500 typography-body-4">
                Loading available AEs...
              </div>
            </div>
          ) : !availableAEs || availableAEs.length === 0 ? (
            <div className="text-red-600 typography-body-4">
              No AEs are set up for this campaign.
            </div>
          ) : (
            <Select
              value={selectedAE?.id || ""}
              onValueChange={(value) => onSelectAE?.(value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select the assigned AE...">
                  {selectedAE &&
                    `${getFullEntityName(selectedAE)} (${selectedAE.email})`}
                </SelectValue>
              </SelectTrigger>

              <SelectContent className="z-[1000] max-h-[300px]">
                {availableAEs.map((ae) => (
                  <SelectItem key={ae.id} value={ae.id}>
                    <div>
                      <div className="typography-body-4-medium">
                        {getFullEntityName(ae)}
                      </div>

                      <div className="text-gray-600 typography-body-5">
                        {ae.email}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : (
        aeName && (
          <div className="mb-4">
            <p className="mb-1 typography-body-4-medium">Meeting with</p>
            <p className="text-base-content/70 typography-body-4">{aeName}</p>
          </div>
        )
      )}

      {/* TODO: isAsyncBooking currently implies Homefix. If other campaigns use
        async booking, gate on a campaign-type flag instead. */}
      {isAsyncBooking && homefixFields && onHomefixFieldChange && (
        <div className="mb-4 space-y-3">
          <div>
            <label
              htmlFor="homefix-address"
              className="mb-1 block typography-body-4-medium"
            >
              Address *
            </label>

            <Input
              id="homefix-address"
              type="text"
              aria-required
              value={homefixFields.address}
              onChange={(e) => onHomefixFieldChange("address", e.target.value)}
              placeholder="Street address"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label
                htmlFor="homefix-city"
                className="mb-1 block typography-body-4-medium"
              >
                City *
              </label>

              <Input
                id="homefix-city"
                type="text"
                aria-required
                value={homefixFields.city}
                onChange={(e) => onHomefixFieldChange("city", e.target.value)}
                placeholder="City"
              />
            </div>

            <div>
              <label className="mb-1 block typography-body-4-medium">
                State *
              </label>

              <Select
                value={homefixFields.state}
                onValueChange={(value) => onHomefixFieldChange("state", value)}
              >
                <SelectTrigger
                  className="w-full"
                  aria-label="State"
                  aria-required
                >
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>

                <SelectContent className="z-[1000]">
                  {HOMEFIX_STATES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label
                htmlFor="homefix-zip"
                className="mb-1 block typography-body-4-medium"
              >
                Zip Code *
              </label>

              <Input
                id="homefix-zip"
                type="text"
                inputMode="numeric"
                aria-required
                value={homefixFields.zip_code}
                onChange={(e) =>
                  onHomefixFieldChange("zip_code", e.target.value)
                }
                placeholder="Zip code"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block typography-body-4-medium">
              Product *
            </label>

            <Select
              value={homefixFields.product}
              onValueChange={(value) => onHomefixFieldChange("product", value)}
            >
              <SelectTrigger
                className="w-full"
                aria-label="Product"
                aria-required
              >
                <SelectValue placeholder="Select a product..." />
              </SelectTrigger>

              <SelectContent className="z-[1000]">
                {HOMEFIX_PRODUCTS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canConfirm && (
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={confirmed ?? false}
                onChange={(e) => onConfirmedChange?.(e.target.checked)}
              />
              <span className="typography-body-4-medium">Confirmed</span>
            </label>
          )}
        </div>
      )}

      <CalendarForm
        bookingDeadlineInMonths={false}
        isNotes
        isNotesRequired={isAsyncBooking}
        notesLabel="Notes"
        notes={notesSeed}
        onDateChange={onDateChange}
        onNotesChange={onNotesChange}
        successButton={{
          text: isLoading
            ? "Finishing..."
            : isReschedule
              ? "Reschedule meeting"
              : "Book meeting",
          type: "button",
          onClick: onBookManually,
          disabled: isLoading || (showAESelector && !selectedAE),
        }}
      />
    </div>
  );
};
